import React, {useCallback} from 'react';
import SentryEchart from "../../../../components/echarts/sentry-echart.jsx";
import {translatedKeys} from "../utils/translated-keys.js";
import {scalePx} from "../../../../utils/responsive-graph.js";

const MAX_VISIBLE_BARS = 4;

const DutyBarGraph = ({
    data,
    title,
    className,
    titleClassName,
    barWidth = "35%",
    barCategoryGap = "20%",
    fontSizeScale = 1,
}) => {
    const buildOption = useCallback((size) => {
        const s = Math.min(size.width, size.height);
        const labelFs = Math.round(scalePx(s, 14, 171, 9, 26) * fontSizeScale);
        const valueFs = Math.round(scalePx(s, 14, 171, 8, 24) * fontSizeScale);

        const entries = Object.entries(data ?? {})
            .sort(([, firstValue], [, secondValue]) => Number(secondValue) - Number(firstValue))
            .slice(0, MAX_VISIBLE_BARS);
        const values = Array.from(
            {length: MAX_VISIBLE_BARS},
            (_, index) => Number(entries[index]?.[1]) || 0
        );
        const y = Array.from({length: MAX_VISIBLE_BARS}, (_, index) => {
            const key = entries[index]?.[0];
            return key == null ? "" : translatedKeys[key] || key || "Não informado";
        });
        const max = Math.max(...values, 1);

        return {
            grid: {
                top: labelFs - 6,
                left: "10%",
                right: '12%',
                bottom: 0,
                outerBounds: {
                    top: labelFs - 6,
                    left: "10%",
                    right: '12%',
                    bottom: 0
                },
                outerBoundsContain: 'axisLabel'
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: 'max-width: 320px; white-space: normal;'
            },
            xAxis: {
                type: 'value',
                max,
                show: false,
            },
            yAxis: {
                type: 'category',
                data: y,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false}
            },
            series: [
                {
                    type: 'bar',
                    barWidth,
                    barCategoryGap,
                    itemStyle: {
                        color: {
                            type: 'linear',
                            x: 0, y: 0, x2: 1, y2: 0,
                            colorStops: [
                                { offset: 0, color: '#003878' },
                                { offset: 0.28, color: '#0080ED' },
                                { offset: 0.52, color: '#1CEAE6' },
                                { offset: 0.72, color: '#3E98D9' },
                                { offset: 1, color: '#003878' },
                            ]
                        },
                        borderRadius: [8]
                    },
                    label: {
                        show: true,
                        position: [0, 0-2],
                        align: 'left',
                        verticalAlign: 'bottom',
                        color: '#AAAAAA',
                        fontSize: labelFs,
                        fontWeight: 400,
                        formatter: (params) => y[params.dataIndex]
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: [8]
                    },
                    data: values
                },
                {
                    type: 'bar',
                    barWidth,
                    barCategoryGap,
                    barGap: '-100%',
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: 'transparent'},
                    label: {
                        show: true,
                        position: 'right',
                        distance: 5,
                        color: '#FFFFFF',
                        fontSize: valueFs,
                        fontWeight: 600,
                        formatter: (params) => y[params.dataIndex] === "" ? "" : values[params.dataIndex]
                    },
                    data: values.map(() => max)
                }
            ]
        }
    }, [barCategoryGap, barWidth, data, fontSizeScale]);

    return (
        <SentryEchart
            title={title}
            titleClass={titleClassName}
            className={className}
            option={buildOption}
        />
    );
};

export default DutyBarGraph;
